import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowLeft, BarChart3, Headphones, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { colors } from 'styles/colors';
import { Helmet } from 'react-helmet';
import { useLanguageContext } from 'context/LanguageContext/LanguageContext';

const HERO_COLLAGE_IMAGE_2 =
  'https://cdn.digitalzone.app/images-from-ibb-upload-to-s3/DZ-151991906';
const HERO_COLLAGE_IMAGE_1 =
  'https://cdn.digitalzone.app/images-from-ibb-upload-to-s3/DZ-888344754';

const stats = [
  { titleId: 'b2b.landing.stats.reliable.title', bodyId: 'b2b.landing.stats.reliable.body' },
  { titleId: 'b2b.landing.stats.diverse.title', bodyId: 'b2b.landing.stats.diverse.body' },
  { titleId: 'b2b.landing.stats.widespread.title', bodyId: 'b2b.landing.stats.widespread.body' },
] as const;

const features = [
  {
    icon: Users,
    titleId: 'b2b.landing.feature.audience.title',
    bodyId: 'b2b.landing.feature.audience.body',
  },
  {
    icon: ShieldCheck,
    titleId: 'b2b.landing.feature.payments.title',
    bodyId: 'b2b.landing.feature.payments.body',
  },
  {
    icon: BarChart3,
    titleId: 'b2b.landing.feature.reports.title',
    bodyId: 'b2b.landing.feature.reports.body',
  },
  {
    icon: Headphones,
    titleId: 'b2b.landing.feature.support.title',
    bodyId: 'b2b.landing.feature.support.body',
  },
] as const;

export const B2bOrganizersLandingPage = () => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const pageDir = language === 'en' ? 'ltr' : 'rtl';
  const pageLang = language === 'en' ? 'en' : 'ar';

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: 'b2b.landing.meta.title',
            defaultMessage: 'Ticket Zone for organizers',
          })}
        </title>
      </Helmet>
      <Box
        bg={colors.b2b.pageBg}
        minH='100vh'
        w='100%'
        overflowX='hidden'
        py={{ base: 6, md: 10 }}
        px={{ base: 3, sm: 4, md: 6 }}
        dir={pageDir}
        lang={pageLang}
      >
        <Container maxW='container.xl' p={0} w='100%'>
          <Box
            lang='ar'
            dir='rtl'
            bg={colors.b2b.landingHeroBg}
            borderTopLeftRadius={{ base: '32px', xl: '36px' }}
            borderTopRightRadius={{ base: '32px', xl: '36px' }}
            borderBottomRightRadius={{ base: '32px', xl: '36px' }}
            borderBottomLeftRadius='0'
            px={{ base: 4, sm: 6, md: 10, lg: 12 }}
            pt={{ base: 8, md: 10, lg: 11 }}
            pb={0}
            overflow='hidden'
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              gap={{ base: 6, lg: 8 }}
              align={{ base: 'stretch', lg: 'flex-start' }}
              justify='space-between'
              pb={{ base: 0, md: 10, lg: 11 }}
              w='100%'
              minW={0}
            >
              <VStack
                align='stretch'
                spacing={0}
                flex='1'
                minW={0}
                textAlign='right'
                pt={{ lg: 1 }}
                maxW={{ base: '100%', lg: 'min(720px, 62%)' }}
              >
                <VStack align='stretch' spacing={{ base: '8px', md: '16px' }} w='100%'>
                  <Flex
                    alignItems='center'
                    justifyContent='center'
                    alignSelf='flex-start'
                    dir='rtl'
                    gap='8px'
                    minW='50.64px'
                    w='131px'
                    maxW='100%'
                    h='35px'
                    minH='35px'
                    py='8px'
                    px='16px'
                    borderWidth='1px'
                    borderColor='white'
                    borderStyle='solid'
                    borderRadius='43px'
                    bg='transparent'
                    opacity={1}
                    boxSizing='border-box'
                    overflow='hidden'
                  >
                    <Sparkles
                      size={17}
                      aria-hidden
                      fill={colors.b2b.landingHeroStar}
                      color={colors.b2b.landingHeroStar}
                      strokeWidth={1.75}
                      style={{ flexShrink: 0 }}
                    />
                    <Text
                      as='span'
                      color='white'
                      fontSize='0.875rem'
                      fontWeight='600'
                      letterSpacing='0.02em'
                      lineHeight='1'
                    >
                      <FormattedMessage
                        id='b2b.landing.hero.badge'
                        defaultMessage='For organizers'
                      />
                    </Text>
                  </Flex>
                  <Heading
                    as='h1'
                    color='white'
                    fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
                    fontWeight='700'
                    fontSize='32px'
                    lineHeight='39.8px'
                    letterSpacing='0'
                    textAlign='right'
                    m={0}
                    wordBreak='break-word'
                    sx={{ verticalAlign: 'middle' }}
                  >
                    <FormattedMessage
                      id='b2b.landing.hero.title'
                      defaultMessage='Register your event and let it reach all of Iraq'
                    />
                  </Heading>
                </VStack>
                <Box w='100%' maxW='795px' minH='84px' mt='4.32px' alignSelf='stretch' opacity={1}>
                  <Text
                    color='rgba(255,255,255,0.95)'
                    fontSize={{ base: '0.9375rem', sm: '1rem', md: '1.0625rem' }}
                    lineHeight={{ base: 1.75, md: 1.65 }}
                    w='100%'
                    fontWeight='400'
                    wordBreak='break-word'
                  >
                    <FormattedMessage
                      id='b2b.landing.hero.subtitle'
                      defaultMessage='Ticket Zone connects you to your audience. Just fill in the details — we handle the rest: ticket sales, payments, promotion, and support.'
                    />
                  </Text>
                </Box>
                <Box
                  alignSelf={{ base: 'stretch', sm: 'flex-start' }}
                  mt={{ base: 6, md: 7 }}
                  w={{ base: '100%', sm: 'auto' }}
                >
                  <Button
                    as={Link}
                    to={ROUTES.b2bRegistration}
                    variant='b2bLandingHeroPrimaryLarge'
                    justifyContent='space-between'
                    gap={3}
                    dir='rtl'
                    w='100%'
                    maxW='352px'
                    sx={{
                      width: '100%',
                      maxWidth: '352px',
                      minWidth: '40.96px',
                      height: '48.57875px',
                      minHeight: '48.57875px',
                      paddingTop: '12.29px',
                      paddingRight: '32.77px',
                      paddingBottom: '12.29px',
                      paddingLeft: '32.77px',
                      borderRadius: '70px',
                      bgGradient: 'none',
                      background: '#FF7A66',
                      border: 'none',
                      _hover: {
                        bgGradient: 'none',
                        background: '#ED6049',
                      },
                      _active: {
                        bgGradient: 'none',
                        background: '#E0563D',
                      },
                    }}
                  >
                    <Text
                      as='span'
                      flex='1'
                      minW={0}
                      textAlign='right'
                      lineHeight='inherit'
                      wordBreak='break-word'
                    >
                      <FormattedMessage
                        id='b2b.landing.cta.startRegistration'
                        defaultMessage='Start registering your event'
                      />
                    </Text>
                    <ArrowLeft size={22} strokeWidth={2.25} aria-hidden style={{ flexShrink: 0 }} />
                  </Button>
                </Box>

                <Flex
                  w='100%'
                  minW={0}
                  gap={{ base: 2, sm: 3, md: 4, lg: 6 }}
                  mt={{ base: 10, md: 12, lg: 18 }}
                  mb={{ base: 10, md: 12 }}
                  py={{ base: 4, md: 5, lg: 6 }}
                  px={0}
                  maxW={{ lg: '100%' }}
                  flex={{ lg: '1 1 auto' }}
                  flexDirection='row'
                  align='stretch'
                  justify='space-between'
                  dir='rtl'
                >
                  {stats.map(({ titleId, bodyId }) => (
                    <Box
                      key={titleId}
                      flex='1'
                      minW={0}
                      borderWidth='1px'
                      borderColor='rgba(255,255,255,0.55)'
                      borderRadius='12px'
                      bg='#FFFFFF12'
                      p={{ base: 2, sm: 3, md: 4, lg: 6 }}
                      dir='rtl'
                      textAlign='right'
                    >
                      <Text
                        color='white'
                        fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
                        fontWeight='800'
                        fontSize={{ base: '18px', lg: '24px' }}
                        lineHeight='24.46px'
                        letterSpacing='0'
                        textAlign='right'
                        mb={2}
                        wordBreak='break-word'
                      >
                        <FormattedMessage id={titleId} defaultMessage={titleId} />
                      </Text>
                      <Text
                        color='rgba(255,255,255,0.9)'
                        fontSize={{ base: '0.625rem', sm: '0.6875rem', md: '0.75rem' }}
                        lineHeight='1.45'
                        fontWeight='400'
                        textAlign='right'
                        wordBreak='break-word'
                      >
                        <FormattedMessage id={bodyId} defaultMessage={bodyId} />
                      </Text>
                    </Box>
                  ))}
                </Flex>
              </VStack>

              <Box
                display={{ base: 'none', lg: 'block' }}
                position='relative'
                flexShrink={0}
                flex={{ lg: '0 0 auto' }}
                w={{ lg: 'min(520px, 100%)' }}
                maxW={{ lg: '520px' }}
                minH={{ lg: '480px' }}
                alignSelf='flex-start'
                aria-hidden
                dir='ltr'
                left={'-150px'}
                bottom={'-50px'}
              >
                {/* Behind (right of pair): clockwise tilt, lower z-index */}
                <Box
                  position='absolute'
                  top='96px'
                  left='152px'
                  w='314.06292165982234px'
                  h='350.5747313131172px'
                  borderRadius='38px'
                  overflow='hidden'
                  borderWidth='5px'
                  borderColor='white'
                  borderStyle='solid'
                  boxShadow='0 16px 40px rgba(0,0,0,0.35)'
                  transform='rotate(10.91deg)'
                  transformOrigin='center center'
                  opacity={1}
                  zIndex={1}
                  boxSizing='border-box'
                >
                  <Image
                    src={HERO_COLLAGE_IMAGE_1}
                    alt=''
                    w='100%'
                    h='100%'
                    objectFit='cover'
                    objectPosition='center 20%'
                    loading='eager'
                    decoding='async'
                  />
                </Box>
                {/* Front (left): upright, overlaps rear from the left */}
                <Box
                  position='absolute'
                  top='48px'
                  left='0px'
                  w='326.83612060546875px'
                  h='418.1454162597656px'
                  borderRadius='38px'
                  overflow='hidden'
                  borderWidth='5px'
                  borderColor='white'
                  borderStyle='solid'
                  boxShadow='0 24px 48px rgba(0,0,0,0.42)'
                  transform='rotate(0deg)'
                  opacity={1}
                  zIndex={2}
                  boxSizing='border-box'
                >
                  <Image
                    src={HERO_COLLAGE_IMAGE_2}
                    alt=''
                    w='100%'
                    h='100%'
                    objectFit='cover'
                    objectPosition='center top'
                    loading='eager'
                    decoding='async'
                  />
                </Box>
              </Box>
            </Flex>
          </Box>

          <Heading
            as='h2'
            color='dark.D800'
            fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
            fontWeight='600'
            fontSize='18px'
            lineHeight='46.31px'
            letterSpacing='0'
            textAlign='right'
            mt={{ base: 12, md: 16 }}
            mb={6}
            wordBreak='break-word'
            sx={{ verticalAlign: 'middle' }}
          >
            <FormattedMessage id='b2b.landing.why.heading' defaultMessage='Why Ticket Zone?' />
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {features.map(({ icon: Icon, titleId, bodyId }) => (
              <Flex
                key={titleId}
                bg={colors.b2b.landingFeatureCardBg}
                borderWidth='1px'
                borderColor={colors.b2b.cardBorder}
                borderRadius='2xl'
                p={{ base: 4, sm: 5, md: 6 }}
                gap={4}
                align='center'
                minW={0}
                boxShadow='0px 2px 12px rgba(15, 23, 42, 0.04)'
              >
                <Flex
                  align='center'
                  justify='center'
                  w={12}
                  h={12}
                  borderRadius='xl'
                  bg={colors.b2b.landingFeatureIconBg}
                  flexShrink={0}
                >
                  <Icon size={24} color={colors.b2b.chipSelected} aria-hidden />
                </Flex>
                <VStack align='start' spacing={2} minW={0} flex='1'>
                  <Text textStyle='subtitleLarge' color='dark.D800' wordBreak='break-word'>
                    <FormattedMessage id={titleId} defaultMessage={titleId} />
                  </Text>
                  <Text textStyle='bodySmallRegular' color='dark.D600' wordBreak='break-word'>
                    <FormattedMessage id={bodyId} defaultMessage={bodyId} />
                  </Text>
                </VStack>
              </Flex>
            ))}
          </SimpleGrid>

          <VStack
            spacing={6}
            mt={{ base: 12, md: 16 }}
            py={{ base: 8, md: 10 }}
            px={{ base: 4, sm: 5, md: 12 }}
            bg={'#FF5C4F1A'}
            borderRadius='2xl'
            align='stretch'
            textAlign='right'
          >
            <Text
              color='dark.D800'
              fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
              fontWeight='700'
              fontSize={{ base: '18px', lg: '32px' }}
              lineHeight='39.8px'
              letterSpacing='0'
              textAlign='right'
              wordBreak='break-word'
              sx={{ verticalAlign: 'middle' }}
            >
              <FormattedMessage
                id='b2b.landing.bottom.prompt'
                defaultMessage='Ready to start? Let’s bring your event to the audience.'
              />
            </Text>
            <Box
              alignSelf={{ base: 'stretch', md: 'flex-start' }}
              w={{ base: '100%', md: 'auto' }}
              display='flex'
              justifyContent={{ base: 'center', md: 'flex-start' }}
            >
              <Button
                as={Link}
                to={ROUTES.b2bRegistration}
                variant='b2bPrimaryLarge'
                display='inline-flex'
                w={{ base: '100%', md: 'auto' }}
                maxW={{ base: '368px', md: 'none' }}
                minW={{ base: '40.96px', md: 'min-content' }}
                justifyContent={{ base: 'space-between', md: 'center' }}
                gap={3}
                borderRadius={{ base: '70px', md: undefined }}
                h={{ base: '48.57875px', md: undefined }}
                minH={{ base: '48.57875px', md: undefined }}
                px={{ base: '32.77px', md: undefined }}
                py={{ base: '12.29px', md: undefined }}
                boxSizing='border-box'
                rightIcon={<ArrowLeft size={20} aria-hidden style={{ flexShrink: 0 }} />}
              >
                <Text
                  as='span'
                  flex={{ base: '1', md: 'unset' }}
                  minW={0}
                  textAlign={{ base: 'start', md: 'inherit' }}
                  lineHeight='inherit'
                  wordBreak='break-word'
                >
                  <FormattedMessage
                    id='b2b.landing.cta.startRegistration'
                    defaultMessage='Start registering your event'
                  />
                </Text>
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  );
};
